import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Input,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@wegroup/design-system';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import {
  useCompanyScanDispatch,
  useCompanyScanSelector,
} from '../../../common/src/utils/globalVariables';
import {
  setCompanyName,
  setPartyId,
  setSelectedCompany,
} from '../../store/redux/slices/companyScanSlice';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  company_name: string;
}

const schema = Yup.object({
  company_name: Yup.string().required('required'),
});

const CbeLookupNotFoundModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const history = useHistory();
  const companyScanDispatch = useCompanyScanDispatch();
  const companyName = useCompanyScanSelector(
    (state) => state.companyScan.companyName,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      company_name: companyName,
    },
  });

  const handleOnConfirm = handleSubmit((values) => {
    companyScanDispatch(setSelectedCompany(undefined));
    companyScanDispatch(setPartyId(undefined));
    companyScanDispatch(setCompanyName(values.company_name));
    history.push({
      pathname: '/company-scan/intro',
      search: `?backRef=${pathname}`,
    });
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('COMPANY-SCAN_CBE-LOOKUP_COMPANY-NOT-FOUND_TITLE')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={VStack} spacing="4">
          <Text>
            {t('COMPANY-SCAN_CBE-LOOKUP_COMPANY-NOT-FOUND_DESCRIPTION')}
          </Text>
          <FormControl isInvalid={!!errors.company_name}>
            <FormLabel>{t('NAME')}</FormLabel>
            <Input
              {...register('company_name')}
              placeholder={t('NAME')}
              data-test-id="COMPANY-SCAN_CBE-LOOKUP_COMPANY-NOT-FOUND_NAME-INPUT"
            />
            {errors.company_name?.message && (
              <FormErrorMessage>
                {t(errors.company_name.message)}
              </FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('CANCEL')}
          </Button>
          <Button
            onClick={handleOnConfirm}
            data-test-id="COMPANY-SCAN_LOOKUP_START-NEW-SCAN"
          >
            {t('COMPANY-SCAN_CBE-LOOKUP-EXISTING_BUTTON_START-NEW')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CbeLookupNotFoundModal;
